import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { api } from "./api";
import type { PublicUser } from "./types";

interface AuthValue {
  user: PublicUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<PublicUser>;
  signup: (input: {
    email: string;
    handle: string;
    password: string;
    displayName?: string;
    isCreator?: boolean;
  }) => Promise<PublicUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  updateMe: (patch: Record<string, unknown>) => Promise<PublicUser>;
}

const AuthCtx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const res = await api.get<{ user: PublicUser | null }>("/api/auth/me");
      setUser(res.user ?? null);
    } catch {
      setUser(null);
    }
  }

  useEffect(() => {
    (async () => { await refresh(); setLoading(false); })();
  }, []);

  const value: AuthValue = {
    user, loading,
    async login(email, password) {
      const res = await api.post<{ user: PublicUser }>("/api/auth/login", { email, password });
      setUser(res.user);
      return res.user;
    },
    async signup(input) {
      const res = await api.post<{ user: PublicUser }>("/api/auth/signup", input);
      setUser(res.user);
      return res.user;
    },
    async logout() {
      await api.post("/api/auth/logout");
      setUser(null);
    },
    async refresh() { await refresh(); },
    async updateMe(patch) {
      const res = await api.patch<{ user: PublicUser }>("/api/me", patch);
      setUser(res.user);
      return res.user;
    },
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
}
